import { useCallback, useEffect, useRef, useState } from "react";
import { API, http } from "@/lib/constants";

// Module-level cache + in-flight dedup so switching window tabs (or mounting
// several widgets that hit the same endpoint) never fires duplicate requests
// and cached windows render instantly. TTLs mirror the server-side cache.
const cache = new Map(); // key -> { ts, data }
const inflight = new Map(); // key -> Promise

const keyOf = (path, params) => `${path}?${new URLSearchParams(params || {}).toString()}`;

export const normalizeErr = (e) => {
  const code = e?.response?.status;
  const body = e?.response?.data;
  if (code === 503) return { code, kind: "unavailable", message: "Asset analytics is temporarily unavailable. Please try again shortly." };
  if (code === 429) return { code, kind: "ratelimit", message: "Analytics is rate limited right now — retrying shortly." };
  if (code === 422) return { code, kind: "params", message: "Invalid request parameters." };
  if (code === 400) return { code, kind: "params", message: (body && body.message) || "Invalid request." };
  if (code === 401) return { code, kind: "auth", message: "Analytics access is not authorized." };
  return { code: code || 0, kind: "network", message: "Couldn't reach analytics. Check your connection and retry." };
};

async function fetchAnalytics(path, params, ttl) {
  const key = keyOf(path, params);
  const hit = cache.get(key);
  if (hit && Date.now() - hit.ts < ttl) return hit.data;
  if (inflight.has(key)) return inflight.get(key);
  const p = http
    .get(`${API}${path}`, { params, timeout: 30000 })
    .then((res) => {
      cache.set(key, { ts: Date.now(), data: res.data });
      inflight.delete(key);
      return res.data;
    })
    .catch((e) => {
      inflight.delete(key);
      throw e;
    });
  inflight.set(key, p);
  return p;
}

// path: e.g. "/assets/stats"; params: object; opts: { ttl, refreshMs }
export function useAnalytics(path, params, { ttl = 60000, refreshMs = 0 } = {}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const paramsKey = keyOf(path, params);
  const genRef = useRef(0);
  const paramsRef = useRef(params);
  paramsRef.current = params;

  const load = useCallback(
    async (soft) => {
      const my = ++genRef.current;
      if (!soft) {
        // Param change (new window/page) or explicit reload: drop the previous
        // window's data so it can't show under the new window's label, and let
        // an error surface even if older rows existed. Background (soft)
        // refreshes keep the current data on a transient failure.
        setLoading(true);
        setData(null);
        setError(null);
      }
      try {
        const d = await fetchAnalytics(path, paramsRef.current, ttl);
        if (genRef.current === my) {
          setData(d);
          setError(null);
        }
      } catch (e) {
        if (genRef.current === my) setError(normalizeErr(e));
      } finally {
        if (genRef.current === my) setLoading(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [path, paramsKey, ttl],
  );

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!refreshMs) return undefined;
    const id = setInterval(() => load(true), refreshMs);
    return () => clearInterval(id);
  }, [load, refreshMs]);

  return { data, loading, error, reload: () => load(false) };
}
